<template>
	<section class="new-event-hotel-checkout-summary">
		<div class="new-event-hotel-checkout-summary__heading">
			<span aria-hidden="true"></span>
			<h3>{{translation.resume}}</h3>
		</div>

		<div class="new-event-hotel-checkout-summary__details">
			<p><strong>{{translation.hotel}}:</strong> {{booking.hotel.name}}</p>
			<p><strong>{{translation.room}}:</strong> {{booking.roomType.name}}</p>
			<p><strong>{{translation.quantity}}:</strong> {{quantityLabel}}</p>
			<p><strong>{{translation.checkin}}:</strong> {{booking.checkin}}</p>
			<p><strong>{{translation.checkout}}:</strong> {{booking.checkout}}</p>
			<p><strong>{{translation.guests}}:</strong> {{guestsLabel}}</p>
		</div>

		<div class="new-event-hotel-checkout-summary__divider"></div>

		<dl class="new-event-hotel-checkout-summary__totals">
			<div>
				<dt>{{translation.dailyPrice}}</dt>
				<dd>{{formatCurrency(dailyPriceWithoutTaxes, booking.price.priceCurrency)}}</dd>
			</div>
			<div>
				<dt>{{translation.taxes}}</dt>
				<dd>{{formatCurrency(taxes, booking.price.priceCurrency)}}</dd>
			</div>
			<div class="new-event-hotel-checkout-summary__total">
				<dt>{{translation.total}}</dt>
				<dd>{{formatCurrency(booking.price.price, booking.price.priceCurrency)}}</dd>
			</div>
		</dl>
	</section>
</template>

<script>
import { translate } from '../../assets/translations';
import AllocationTypes from '../../enums/AllocationTypes';

export default {
	props: {
		booking: {
			type: Object,
			required: true,
		},
	},
	computed: {
		allocationTypeText() {
			const allocationType = this.booking.price
				&& this.booking.price.roomAllocation
				? this.booking.price.roomAllocation.allocationType
				: null;
			return AllocationTypes[allocationType]
				? this.translation[AllocationTypes[allocationType].nameProperty]
				: '';
		},
		dailyPriceWithoutTaxes() {
			return this.normalizeCurrencyValue(this.booking.price.price) - this.taxes;
		},
		guestsLabel() {
			const guests = [];
			this.booking.rooms.forEach(room => {
				(room.guests || []).forEach(guest => {
					if (guest.name) guests.push(guest.name);
				});
			});
			return guests.join(', ');
		},
		quantityLabel() {
			const roomsQty = this.booking.rooms ? this.booking.rooms.length : 0;
			const allocationType = this.allocationTypeText ? ` ${this.allocationTypeText}` : '';
			return `${roomsQty} ${this.booking.roomType.name}${allocationType}`;
		},
		taxes() {
			return this.normalizeCurrencyValue(this.booking.price.price) * 0.10;
		},
		translation() {
			return translate(this.$store.state.language).hotelBookingPersonalInfo;
		},
	},
	methods: {
		formatCurrency(value, currency) {
			const symbol = currency && currency.symbol ? currency.symbol : 'R$';
			const number = this.normalizeCurrencyValue(value).toLocaleString('pt-BR', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			return `${symbol} ${number}`;
		},
		normalizeCurrencyValue(value) {
			if (value === null || value === undefined || value === '') return 0;
			if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

			let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
			if (!normalizedValue) return 0;

			const lastComma = normalizedValue.lastIndexOf(',');
			const lastDot = normalizedValue.lastIndexOf('.');

			if (lastComma !== -1 && lastDot !== -1) {
				normalizedValue = lastComma > lastDot
					? normalizedValue.replace(/\./g, '').replace(',', '.')
					: normalizedValue.replace(/,/g, '');
			} else if (lastComma !== -1) {
				normalizedValue = normalizedValue.replace(',', '.');
			} else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalizedValue)) {
				normalizedValue = normalizedValue.replace(/\./g, '');
			}

			const numberValue = Number(normalizedValue);
			return Number.isFinite(numberValue) ? numberValue : 0;
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-hotel-checkout-summary {
	box-sizing: border-box;
	min-height: 567px;
	padding: 45px 66px 52px;
	border-radius: 35px 35px 0 0;
	background: #eeeeee;
	color: #5a5a5a;
}

.new-event-hotel-checkout-summary__heading {
	margin-bottom: 58px;
}

.new-event-hotel-checkout-summary__heading span {
	display: block;
	width: 23px;
	height: 12px;
	margin: 0 0 27px;
	background: url("data:image/svg+xml,%3Csvg width='23' height='12' viewBox='0 0 23 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 2L11.5 10L21 2' stroke='%2366ACC6' stroke-width='3'/%3E%3C/svg%3E") center / contain no-repeat;
}

.new-event-hotel-checkout-summary__heading h3 {
	margin: 0;
	color: #002c41;
	font-size: 18px;
	font-weight: 700;
	line-height: 21px;
}

.new-event-hotel-checkout-summary__details {
	font-size: 18px;
	font-weight: 700;
	line-height: 40px;
}

.new-event-hotel-checkout-summary__details p {
	margin: 0;
}

.new-event-hotel-checkout-summary__divider {
	height: 1px;
	margin: 39px 0 29px;
	background: rgba(90, 90, 90, .6);
}

.new-event-hotel-checkout-summary__totals {
	display: grid;
	gap: 0;
	margin: 0;
	font-size: 18px;
	line-height: 40px;
}

.new-event-hotel-checkout-summary__totals div {
	display: flex;
	justify-content: space-between;
	gap: 30px;
}

.new-event-hotel-checkout-summary__totals dt,
.new-event-hotel-checkout-summary__totals dd {
	margin: 0;
}

.new-event-hotel-checkout-summary__totals dt,
.new-event-hotel-checkout-summary__totals dd {
	font-weight: 500;
}

.new-event-hotel-checkout-summary__total dt,
.new-event-hotel-checkout-summary__total dd {
	font-weight: 700;
}

@media (max-width: 900px) {
	.new-event-hotel-checkout-summary {
		min-height: 0;
		padding: 34px 24px 38px;
		border-radius: 24px 24px 0 0;
	}

	.new-event-hotel-checkout-summary__heading {
		margin-bottom: 34px;
	}

	.new-event-hotel-checkout-summary__details,
	.new-event-hotel-checkout-summary__totals {
		font-size: 15px;
		line-height: 28px;
	}

	.new-event-hotel-checkout-summary__totals div {
		display: grid;
		gap: 2px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/hotel-reservation/HotelBookingResume.vue